import { getLatest, searchContent } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { type ContentGraph, childrenOf, jsonValueOf, readGraph, readTree } from './graph';
import { smLibraryPassageSchema, smLibraryValueSchema, smPassageTextSchema } from './schemas';

/**
 * The read-aloud library (scripts/seeds/read-aloud) as Core serves it: one `SM Library`
 * container holding its passages in order, each passage's text one level further down and
 * its two covers as File nodes under the text. Listing reads one level only, so no text
 * travels with it; opening a passage reads its text.
 */
export const READING_LIBRARY_HANDLE = 'read-aloud/en-US';

export interface LibraryCategory {
  key: string;
  /** As the library names it, in English; screens translate the keys they know. */
  label: string;
}

export interface LibraryBook {
  lineageUuid: string;
  /** The version read: what Saved points at. */
  uuid: string;
  key: string;
  title: string;
  author?: string;
  /** The kind of text ("abridged-story"); screens translate it into the tag. */
  kind?: string;
  /** The kind as the library words it ("Short story — abridged"), for kinds the app doesn't know. */
  label?: string;
  categories: string[];
  /** Two sentences about the book (Library › book info). */
  summary?: string;
  /** Estimated reading time, whole minutes (at least 1). */
  minutes?: number;
  /** File content uuids of the covers: `card` 198 × 141, `row` 64 × 123. */
  cover?: { background?: string; card?: string; row?: string };
}

export interface ReadingLibrary {
  categories: LibraryCategory[];
  books: LibraryBook[];
}

export const EMPTY_READING_LIBRARY: ReadingLibrary = { categories: [], books: [] };

/** Types the library needs; the environment has the library only when all three are set. */
export type LibraryTypes = Required<
  Pick<StrongerMemoryTypes, 'passage' | 'library' | 'libraryKey' | 'passageText'>
>;

export function libraryTypes(types: StrongerMemoryTypes | undefined): LibraryTypes | undefined {
  if (!types?.passage || !types.library || !types.libraryKey || !types.passageText) return;
  return {
    passage: types.passage,
    library: types.library,
    libraryKey: types.libraryKey,
    passageText: types.passageText,
  };
}

/**
 * The library, picked out by its handle and by its author: only the programme group's, so a
 * look-alike someone else made public can never stand in for it.
 */
export async function loadReadingLibrary(
  types: LibraryTypes,
  groupActorUuid: string | undefined,
): Promise<ReadingLibrary> {
  const response = await searchContent({
    rootContentTypeUuids: [types.library],
    predicates: [
      {
        pathTypeUuids: [types.libraryKey],
        op: 'eq',
        valueType: 'STRING',
        value: READING_LIBRARY_HANDLE,
      },
    ],
    ...(groupActorUuid
      ? { actorPredicates: [{ edgeTypes: ['AUTHORED_BY'], actorUuids: [groupActorUuid] }] }
      : {}),
    maxDepth: 1,
    first: 1,
  });
  if (response.status !== 200) {
    throw new Error(`OnEarly: library search failed (${response.status})`);
  }
  return readingLibraryFromGraph(readGraph(response.data), types);
}

export function readingLibraryFromGraph(graph: ContentGraph, types: LibraryTypes): ReadingLibrary {
  const root = graph.roots[0] ? graph.nodes.get(graph.roots[0]) : undefined;
  if (!root) return EMPTY_READING_LIBRARY;
  const library = smLibraryValueSchema.safeParse(jsonValueOf(root));
  const books: LibraryBook[] = [];
  // In the library's order (CONTAINS position); retired passages stay in Core for history.
  for (const node of childrenOf(graph, root.uuid)) {
    if (node.contentTypeUuid !== types.passage) continue;
    const parsed = smLibraryPassageSchema.safeParse(jsonValueOf(node));
    if (!parsed.success || parsed.data.retired) continue;
    const { key, title, author, kind, label, categories, summary, minutes, cover } = parsed.data;
    books.push({
      lineageUuid: node.lineageUuid,
      uuid: node.uuid,
      key,
      title,
      categories,
      ...(author ? { author } : {}),
      ...(kind ? { kind } : {}),
      ...(label ? { label } : {}),
      ...(summary ? { summary } : {}),
      ...(minutes ? { minutes: Math.max(1, Math.round(minutes.at180wpm)) } : {}),
      ...(cover
        ? {
            cover: {
              ...(cover.background ? { background: cover.background } : {}),
              ...(cover.foryou ? { card: cover.foryou } : {}),
              ...(cover.savedtall ? { row: cover.savedtall } : {}),
            },
          }
        : {}),
    });
  }
  return { categories: library.success ? library.data.categories : [], books };
}

/** A library passage with its text, ready to read aloud. */
export interface LibraryPassage {
  lineageUuid: string;
  title: string;
  author?: string;
  body: string;
}

/** One passage and its text. `undefined` when the lineage isn't a library passage. */
export async function loadLibraryPassage(
  types: LibraryTypes,
  lineageUuid: string,
): Promise<LibraryPassage | undefined> {
  const response = await getLatest(lineageUuid);
  if (response.status === 404) return undefined;
  if (response.status !== 200) {
    throw new Error(`OnEarly: reading the passage failed (${response.status})`);
  }
  return libraryPassageFromTree(readTree(response.data), types, lineageUuid);
}

export function libraryPassageFromTree(
  graph: ContentGraph,
  types: LibraryTypes,
  lineageUuid: string,
): LibraryPassage | undefined {
  const node = [...graph.nodes.values()].find(
    (candidate) =>
      candidate.lineageUuid === lineageUuid && candidate.contentTypeUuid === types.passage,
  );
  if (!node) return undefined;
  const passage = smLibraryPassageSchema.safeParse(jsonValueOf(node));
  if (!passage.success) return undefined;
  const textNode = childrenOf(graph, node.uuid).find(
    (child) => child.contentTypeUuid === types.passageText,
  );
  const text = textNode ? smPassageTextSchema.safeParse(jsonValueOf(textNode)) : undefined;
  if (!text?.success) return undefined;
  return {
    lineageUuid,
    title: passage.data.title,
    body: text.data.body,
    ...(passage.data.author ? { author: passage.data.author } : {}),
  };
}

/** Where a cover downloads: Core serves PUBLIC files without a bearer. */
export function coverUrl(onearlyApiUrl: string, fileUuid: string): string {
  return `${onearlyApiUrl}/api/download/file/${fileUuid}`;
}
